/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  *
  * Analysis primitives for the Claude usage panel: grouping, deltas, derivation facts and
  * CSV. Pure functions, kept out of the component for the reason claudeMetricsView.ts
  * states -- the interesting bugs here are arithmetic and aggregation, not JSX.
  *
  * The governing rule, and the reason this file exists rather than a chart library: an
  * aggregate is only shown when it is exact. Where the current grouping makes a figure
  * inexact, it is withheld AND the reason travels with it, so the panel can say why instead
  * of printing something plausible.
  */
import {
  RunSaving,
  Totals,
  UNKNOWN_ACTOR,
  WeeklyRow,
  isCoverageMethod,
  totals,
} from './claudeMetricsView';

/**
 * Every dimension an analyst can group or filter by. `team` is a CLIENT-SIDE
 * projection of repo through the catalog's ownership map -- the panel enriches
 * rows before grouping, so the wire and the stores never carry it.
 */
export const DIMENSIONS = ['org', 'repo', 'team', 'model', 'author', 'trigger'] as const;
/**
 * PR rides along at EVERY granularity now: week, day and month are all read-time
 * rollups of raw runs (every reachable week lies inside raw retention), and raw
 * runs carry the PR. The persisted weekly table still has no pr column -- it is
 * the archival record, not the serving path.
 *
 * savingsMethod is a SAVINGS-FIRST dimension: its groups are built from the
 * run-grain savings cells, not the token rows -- a run can report several
 * methods, so method groups OVERLAP on runs and no token, cost or message figure
 * can be attributed to one without double-counting. Those columns are withheld
 * under it (tokensWithheldBy names why); savings dollars and per-method run
 * counts stay exact. It cannot compose with `model` (savings cells carry none).
 */
export const DAY_DIMENSIONS = [...DIMENSIONS, 'job', 'pr', 'savingsMethod'] as const;
export type Dimension = (typeof DAY_DIMENSIONS)[number];

export const DIMENSION_LABELS: Record<Dimension, string> = {
  org: 'Org',
  repo: 'Repo',
  team: 'Team',
  model: 'Model',
  author: 'PR author',
  trigger: 'Run trigger',
  job: 'Run type',
  pr: 'PR',
  // 'Savings', not 'Savings method': the picker truncates the long form to
  // "Savings meth", and the grouping is recognizably the savings pivot.
  savingsMethod: 'Savings',
};

/**
 * Review-quality aggregate for a group (PLAT-1819): computed from the finding
 * grain by the join module, exact under joinable dimensions only. Advisory
 * rows are counted apart and excluded from the fix-rate denominator.
 */
export type ReviewCell = {
  findings: number;
  fixed: number;
  open: number;
  closed: number;
  declined: number;
  advisory: number;
  /** fixed ÷ findings; absent when the group tracked none (never 0%). */
  fixRate?: number;
};

/** Why a group's review cells are withheld (the reason travels to the cell). */
export type ReviewWithheld = Dimension | 'window' | 'truncated';

export type GroupNode = {
  /** Full path from the root -- stable across renders and across weeks, so deltas can key on it. */
  path: string;
  dim: Dimension;
  value: string;
  totals: Totals;
  /**
   * Distinct runs, present ONLY when every row under this node shares one model.
   *
   * A run reports once per model, so a node spanning two models would count that run twice.
   * Rather than print an inflated figure, it is withheld and `runCountBlockedBy` records
   * what made it inexact.
   */
  runCount?: number;
  runCountBlockedBy?: 'model';
  /** Grain rows underneath. Surfaced in the derivation panel. */
  rowCount: number;
  /**
   * The runs' own reported metering (total_cost_usd), summed once per distinct
   * run from the run-grain records. Present exactly where the run count is
   * derivable that way -- a group that splits runs by model has no honest cost,
   * so it is withheld (runCountBlockedBy names why), never approximated. There
   * is deliberately NO price table in this tool: list prices change without
   * notice, and an unmaintained rate is a confidently wrong dollar figure.
   */
  reportedCostUsd?: number;
  /** Of the runs under this node, how many carried a reported cost. */
  costedRuns?: number;
  totalRuns?: number;
  /**
   * The node's ATTRIBUTABLE reported savings (counted methods only), summed once
   * per distinct run from the run-grain savings cells; ambient pricing effects
   * (prompt-cache) are excluded except inside a savings-method subtree, where
   * the cells are already reduced to one method. Present only where a counted
   * method exists under the node -- a group whose runs reported only ambient or
   * coverage methods prints absence, never a silent $0.00 (same rule as
   * reportedCostUsd).
   */
  savingsUsd?: number;
  /**
   * Distinct runs under this node with at least one COUNTED savings method
   * (server-computed attrSavedRuns). May be undefined while savingsUsd is set:
   * a pre-field cell withholds the run claim rather than approximating it.
   */
  savedRuns?: number;
  /**
   * Set on a savings-method group whose method reports COVERAGE, not dollars
   * (see isCoverageMethod). Its savingsUsd is a truthful zero, but printing it
   * as $0.00 beside priced methods reads as "saved nothing" when it means
   * "reached these runs" -- so the cell prints the coverage instead.
   */
  coverage?: boolean;
  /**
   * Set on savings-method groups (and their descendants): token, message and cost
   * figures are withheld there because method groups overlap on runs -- a run
   * reporting two methods appears under both, and summing anything per-run across
   * them double-counts. Savings dollars stay exact per method.
   */
  tokensWithheldBy?: 'savingsMethod';
  /**
   * Distinct orgs beneath this node, sorted.
   *
   * Needed because a `repo` node carries only the repo name, and repo names are not unique
   * across orgs: resolving an owner by matching the map key's suffix would attribute
   * `m42-games/core` to whichever `<org>/core` happened to be first. On a chargeback
   * surface that is a wrong answer, not a slow one.
   */
  orgs: string[];
  /**
   * Review-quality figures joined from the finding grain (PLAT-1819). Present
   * exactly where every path segment above is a joinable dimension; withheld
   * with the reason otherwise. Attached post-build by attachReviewQuality —
   * the token tree never depends on the findings payload arriving.
   */
  review?: ReviewCell;
  reviewWithheldBy?: ReviewWithheld;
  /**
   * Whether any grain row under this node came from the pr-review job — the
   * gate for the run drill-down (rows without a job field, one deploy behind,
   * stay false: no expander is offered on unprovable data).
   */
  hasReviewRuns?: boolean;
  children: GroupNode[];
};

/**
 * Path separator.
 *
 * A pipe is safe rather than merely unlikely: every value that can appear here comes from
 * `ID_CHARSET` in claudeMetricsMessage.ts, which admits only `[A-Za-z0-9._:@-]`. So no org,
 * repo, model or login can contain one, and two different paths cannot collide.
 */
const SEP = '|';

/**
 * Groups rows into a tree following `dims` in order.
 *
 * Generic on purpose: `['org','repo','model']` reproduces the original fixed tree, while
 * `['author','repo']` answers chargeback and `['trigger','author']` answers who generates
 * review load, from the same payload with no refetch.
 */
/** Sortable columns: each key extracts the number a comparator ranks groups by. */
export type SortKey =
  | 'total'
  | 'cost'
  | 'savings'
  | 'findings'
  | 'fixRate'
  | 'input'
  | 'output'
  | 'cacheWrite'
  | 'cacheRead'
  | 'msgs'
  | 'runs';
export type Sort = { key: SortKey; dir: 'asc' | 'desc' };

const SORT_EXTRACT: Record<SortKey, (n: GroupNode) => number> = {
  total: n => n.totals.totalTokens,
  cost: n => n.reportedCostUsd ?? -Infinity, // withheld sorts past every real figure
  savings: n => n.savingsUsd ?? -Infinity, // absent (no savings reported) sorts last
  findings: n => n.review?.findings ?? -Infinity, // withheld sorts past every count
  fixRate: n => n.review?.fixRate ?? -Infinity, // untracked and withheld sort last
  input: n => n.totals.inputTokens,
  output: n => n.totals.outputTokens,
  cacheWrite: n => n.totals.cacheWriteTokens,
  cacheRead: n => n.totals.cacheReadTokens,
  msgs: n => n.totals.msgs,
  runs: n => n.runCount ?? -Infinity, // withheld ('mixed') sorts past exact counts
};

/**
 * A run-grain record for exact run counting: the run-cost entries the server
 * ships beside the rows, optionally enriched with `team` by the panel (the same
 * projection the rows get). One entry per (org, repo, author, trigger[, pr])
 * cell with a DISTINCT-run total, so summing entries across any of those
 * dimensions can never double-count a run -- unlike the per-model rows.
 */
export type RunCountSource = {
  org: string;
  repo: string;
  author: string;
  trigger: string;
  pr?: string;
  /** Run type (GitHub job key). A run has exactly one, so counts stay exact. */
  job?: string;
  team?: string;
  totalRuns: number;
  /** Runs in the cell that carried a reported cost; < totalRuns = partial coverage. */
  costedRuns: number;
  /** The cell's reported spend, counted once per distinct run at the source. */
  costUsd: number;
};

/**
 * The run-grain savings cells, optionally enriched with `team` by the panel --
 * the same projection rows and run-cost records get. Same pre-filtering
 * contract as RunCountSource.
 */
export type RunSavingSource = RunSaving & { team?: string };

export function groupRows(
  rows: readonly WeeklyRow[],
  dims: readonly Dimension[],
  sort: Sort = { key: 'total', dir: 'desc' },
  /**
   * When present, model-spanning groups derive their run count from these
   * records instead of being withheld. The caller must pre-filter them by the
   * SAME narrowing as `rows` -- and must omit them entirely under a model
   * narrowing, where every group is single-model and the rows are exact anyway.
   */
  runCounts?: readonly RunCountSource[],
  /**
   * When present, groups carry their reported savings, and the savingsMethod
   * dimension becomes buildable. Same pre-filtering contract as runCounts, and
   * the same model rule: savings cells carry no model, so they are dropped
   * below one.
   */
  savings?: readonly RunSavingSource[],
): GroupNode[] {
  if (!dims.length) return [];
  return build(rows, dims, '', sort, runCounts, savings);
}

/** Shared comparator: sort key, then a stable tiebreak so renders never reshuffle. */
function sortNodes(nodes: GroupNode[], sort: Sort): GroupNode[] {
  return nodes.sort((a, b) => {
    const extract = SORT_EXTRACT[sort.key];
    const diff =
      sort.dir === 'desc' ? extract(b) - extract(a) : extract(a) - extract(b);
    return diff || a.value.localeCompare(b.value);
  });
}

/**
 * Re-sorts an already-built tree in place, every level. The review columns are
 * attached AFTER groupRows built (and sorted) the tree, so a review-backed sort
 * key must sort again — inside build() every node's `review` was still
 * undefined, and the comparator silently fell through to the alphabetical
 * tiebreak.
 */
export function resortTree(nodes: GroupNode[], sort: Sort): void {
  sortNodes(nodes, sort);
  for (const n of nodes) resortTree(n.children, sort);
}

/** The dim value of a savings cell; '_none_' where the cell carries none. */
function savingValue(c: RunSavingSource, dim: Dimension): string {
  if (dim === 'pr') return c.pr ?? '_none_';
  if (dim === 'job') return c.job ?? '_none_';
  if (dim === 'team') return c.team ?? '_none_';
  if (dim === 'org' || dim === 'repo' || dim === 'author' || dim === 'trigger') {
    return c[dim];
  }
  return '_none_'; // model / savingsMethod never reach here
}

function build(
  rows: readonly WeeklyRow[],
  dims: readonly Dimension[],
  prefix: string,
  sort: Sort,
  runCounts?: readonly RunCountSource[],
  savings?: readonly RunSavingSource[],
): GroupNode[] {
  const [dim, ...rest] = dims;
  if (dim === 'savingsMethod') {
    return buildMethodLevel(rest, prefix, sort, runCounts, savings);
  }
  const buckets = new Map<string, WeeklyRow[]>();
  for (const r of rows) {
    // 'pr' exists only on day rows; a weekly row reaching a pr grouping (it cannot,
    // via the panel) buckets under the sentinel rather than the string 'undefined'.
    const value = r[dim] ?? '_none_';
    const at = buckets.get(value);
    if (at) at.push(r);
    else buckets.set(value, [r]);
  }

  // The run-grain records bucket along the same value strings as the rows --
  // identical sentinels, identical qualified-PR scheme, identical team
  // projection -- except for 'model', which run-grain records do not carry.
  // Below a model level the records are dropped: every such group is
  // single-model and the rows count exactly there.
  const countBuckets =
    runCounts && dim !== 'model' ? new Map<string, RunCountSource[]>() : undefined;
  if (countBuckets && runCounts) {
    for (const c of runCounts) {
      const value = (dim === 'pr' ? c.pr : c[dim as keyof RunCountSource]) ?? '_none_';
      const at = countBuckets.get(value as string);
      if (at) at.push(c);
      else countBuckets.set(value as string, [c]);
    }
  }

  // Savings cells bucket exactly like the count records (same grain, same model
  // rule: no model on the cells, so they drop below one).
  const savingsBuckets =
    savings && dim !== 'model' ? new Map<string, RunSavingSource[]>() : undefined;
  if (savingsBuckets && savings) {
    for (const c of savings) {
      const value = savingValue(c, dim);
      const at = savingsBuckets.get(value);
      if (at) at.push(c);
      else savingsBuckets.set(value, [c]);
    }
  }
  // Under a savings-method node the token rows are gone (tokens cannot follow a
  // method split), so the cells seed the value set instead.
  const inMethodSubtree = rows.length === 0 && !!savings?.length;
  if (inMethodSubtree && savingsBuckets) {
    for (const value of savingsBuckets.keys()) {
      if (!buckets.has(value)) buckets.set(value, []);
    }
  }

  return sortNodes(
    [...buckets.entries()].map(([value, bucket]) => {
      const path = prefix ? `${prefix}${SEP}${value}` : value;
      const models = new Set(bucket.map(r => r.model));
      const countBucket = countBuckets?.get(value);
      const savingsBucket = savingsBuckets?.get(value);
      const node: GroupNode = {
        path,
        dim,
        value,
        totals: totals(bucket),
        rowCount: bucket.length,
        orgs: [
          ...new Set([
            ...bucket.map(r => r.org),
            ...(inMethodSubtree ? (savingsBucket ?? []).map(c => c.org) : []),
          ]),
        ].sort(),
        hasReviewRuns: bucket.some(r => r.job === 'claude-review'),
        children: rest.length
          ? build(bucket, rest, path, sort, countBucket, savingsBucket)
          : [],
      };
      if (countBucket) {
        // Reported cost rides the run-grain records wherever they reach: exact
        // across every dimension a run has one of, withheld where model splits it.
        node.costedRuns = countBucket.reduce((n, c) => n + c.costedRuns, 0);
        node.totalRuns = countBucket.reduce((n, c) => n + c.totalRuns, 0);
        // Only when at least one run actually reported a cost: a zero-coverage
        // group summing to $0.00 reads as FREE, and "confidently wrong dollar
        // figure" is the exact failure this surface exists to refuse. The
        // coverage counters stay -- "0 of N runs" is the honest cell.
        if (node.costedRuns > 0) {
          node.reportedCostUsd = countBucket.reduce((n, c) => n + c.costUsd, 0);
        }
      }
      if (savingsBucket && savingsBucket.length) {
        if (inMethodSubtree) {
          // Under a method node the cells are already REDUCED to that one
          // method, so its figures pass through whole — including an ambient
          // method's, whose subtree is exactly where its per-repo split is
          // legitimately explorable.
          node.savedRuns = savingsBucket.reduce((n, c) => n + c.savedRuns, 0);
          node.savingsUsd = savingsBucket.reduce((n, c) => n + c.savingsUsd, 0);
        } else {
          // ATTRIBUTABLE-only figures (reclassification, 2026-08-26): the
          // Savings column carries methods an enforcement hook measured
          // (counted uses); ambient pricing effects (priced-but-uncounted,
          // i.e. prompt-cache) stay out — the cost column already reflects
          // that discount. Classification is per method ACROSS the bucket,
          // the same rule reportedSavings applies to the tile: a method
          // counted anywhere in the bucket is attributable with its whole
          // figure, so the tile always equals the sum of its rows even when
          // a producer rollout leaves some cells uncounted. Presence still
          // means real: the figure is set only when a counted method exists,
          // so a prompt-cache-only or coverage-only group renders absence,
          // never a silent $0.00 — while a counted method's honest zero
          // (counts shipped before the estimate model) still shows.
          const perMethod = new Map<string, { usd: number; countedRuns: number }>();
          for (const c of savingsBucket) {
            for (const m of c.methods) {
              const at = perMethod.get(m.method) ?? { usd: 0, countedRuns: 0 };
              at.usd += m.usd;
              at.countedRuns += m.countedRuns ?? 0;
              perMethod.set(m.method, at);
            }
          }
          const counted = [...perMethod.values()].some(m => m.countedRuns > 0);
          if (counted) {
            node.savingsUsd = [...perMethod.values()].reduce(
              (n, m) => (m.countedRuns > 0 ? n + m.usd : n),
              0,
            );
            // Distinct attributable runs come from the server (method run
            // counts overlap); one pre-field cell withholds the claim.
            if (savingsBucket.every(c => c.attrSavedRuns !== undefined)) {
              node.savedRuns = savingsBucket.reduce(
                (n, c) => n + (c.attrSavedRuns ?? 0),
                0,
              );
            }
          }
        }
      }
      if (inMethodSubtree) {
        // Tokens/messages/cost stay withheld through the whole method subtree;
        // the run count IS exact here (one method's cells partition its runs).
        node.tokensWithheldBy = 'savingsMethod';
        node.runCount = node.savedRuns ?? 0;
        // Coverage rides the subtree for the same reason: every cell down here
        // was reduced to the ONE method at its root, so a descendant is
        // coverage exactly when its root is. Without this a group of
        // savingsMethod -> anything re-renders the $0.00 that the method row
        // above it just stopped showing.
        //
        // Guarded on a non-empty bucket on purpose: [].every() is true, and an
        // empty bucket must never mint a coverage claim out of nothing.
        if (savingsBucket?.length) {
          node.coverage = savingsBucket.every(c => c.methods.every(isCoverageMethod));
        }
      } else if (models.size === 1) {
        // Exact: within one model each run is counted once, and a run has exactly one
        // (author, trigger) pair, so summing across the actor grains cannot double-count.
        node.runCount = bucket.reduce((n, r) => n + r.runCount, 0);
      } else if (countBucket) {
        // Exact THROUGH the model split: the run-grain records partition runs by
        // dimensions a run has exactly one of, so their distinct-run totals sum
        // cleanly where the per-model rows would double-count.
        node.runCount = countBucket.reduce((n, c) => n + c.totalRuns, 0);
      } else {
        node.runCountBlockedBy = 'model';
      }
      return node;
    }),
    sort,
  );
}

/**
 * The savingsMethod level: one node per method, built from the savings cells.
 *
 * Method groups OVERLAP on runs (a run reporting two methods sits under both), so
 * token, message and cost figures are structurally unattributable here and stay
 * withheld; each method's own dollars and run count are exact. A trailing
 * `_none_` node carries the runs that reported no savings at all -- computed per
 * cell as totalRuns minus that cell's savedRuns, so the overlap cannot distort
 * it. Children keep grouping the method's REDUCED cells (that one method's
 * figures) by the remaining run-grain dimensions.
 */
function buildMethodLevel(
  rest: readonly Dimension[],
  prefix: string,
  sort: Sort,
  runCounts?: readonly RunCountSource[],
  savings?: readonly RunSavingSource[],
): GroupNode[] {
  const zero = totals([]);
  const byMethod = new Map<string, RunSavingSource[]>();
  for (const c of savings ?? []) {
    for (const m of c.methods) {
      // The cell reduced to ONE method: children under this node must not
      // re-count the cell's other methods.
      const reduced: RunSavingSource = {
        ...c,
        savingsUsd: m.usd,
        savedRuns: m.runs,
        totalRuns: m.runs,
        methods: [m],
      };
      const at = byMethod.get(m.method);
      if (at) at.push(reduced);
      else byMethod.set(m.method, [reduced]);
    }
  }

  const nodes: GroupNode[] = [...byMethod.entries()].map(([method, cells]) => {
    const path = prefix ? `${prefix}${SEP}${method}` : method;
    const savedRuns = cells.reduce((n, c) => n + c.savedRuns, 0);
    return {
      path,
      dim: 'savingsMethod' as const,
      value: method,
      totals: { ...zero },
      tokensWithheldBy: 'savingsMethod' as const,
      rowCount: 0,
      runCount: savedRuns,
      savedRuns,
      savingsUsd: cells.reduce((n, c) => n + c.savingsUsd, 0),
      // Every cell here was reduced to this one method, so its shape decides
      // the group: coverage only when NO cell priced or counted it.
      coverage: cells.every(c => c.methods.every(isCoverageMethod)),
      orgs: [...new Set(cells.map(c => c.org))].sort(),
      children: rest.length ? build([], rest, path, sort, undefined, cells) : [],
    };
  });

  // Runs that reported no savings. Per-cell arithmetic keeps the count exact
  // under method overlap; it needs the cost records for the denominators, so it
  // is simply absent without them (never guessed).
  if (runCounts?.length) {
    const savedByKey = new Map<string, number>();
    for (const c of savings ?? []) {
      const key = [c.org, c.repo, c.author, c.trigger, c.pr ?? '', c.job ?? ''].join(SEP);
      savedByKey.set(key, (savedByKey.get(key) ?? 0) + c.savedRuns);
    }
    let noneRuns = 0;
    const noneOrgs = new Set<string>();
    for (const c of runCounts) {
      const key = [c.org, c.repo, c.author, c.trigger, c.pr ?? '', c.job ?? ''].join(SEP);
      const none = c.totalRuns - (savedByKey.get(key) ?? 0);
      if (none > 0) {
        noneRuns += none;
        noneOrgs.add(c.org);
      }
    }
    if (noneRuns > 0) {
      nodes.push({
        path: prefix ? `${prefix}${SEP}_none_` : '_none_',
        dim: 'savingsMethod',
        value: '_none_',
        totals: { ...zero },
        tokensWithheldBy: 'savingsMethod',
        rowCount: 0,
        runCount: noneRuns,
        orgs: [...noneOrgs].sort(),
        children: [],
      });
    }
  }
  return sortNodes(nodes, sort);
}

/**
 * Prunes the group tree to rows matching a text query -- the Ctrl-F the browser
 * cannot do against a sticky, collapsible, scrolling table.
 *
 * DISPLAY pruning, not re-aggregation, on purpose: a node that matches keeps its
 * whole subtree, a node with a matching descendant stays visible as the path to
 * it, and every figure that remains on screen is still that group's TRUE total.
 * Re-slicing totals to the visible rows would silently re-answer a different
 * question ("what did the matching rows spend") than the row answers ("what did
 * this group spend") -- the caption names the pruning instead.
 */
export function filterGroups(
  groups: readonly GroupNode[],
  query: string,
  label: (node: GroupNode) => string,
): { groups: readonly GroupNode[]; matchedTop: number; totalTop: number } {
  const q = query.trim().toLowerCase();
  if (!q) return { groups, matchedTop: groups.length, totalTop: groups.length };
  const prune = (nodes: readonly GroupNode[]): GroupNode[] => {
    const out: GroupNode[] = [];
    for (const n of nodes) {
      if (label(n).toLowerCase().includes(q)) {
        out.push(n); // the match anchors its whole subtree
        continue;
      }
      const kids = prune(n.children);
      if (kids.length) out.push({ ...n, children: kids });
    }
    return out;
  };
  const pruned = prune(groups);
  return { groups: pruned, matchedTop: pruned.length, totalTop: groups.length };
}

export type Delta = { absolute: number; pct?: number; isNew: boolean };

/**
 * Change in total tokens per group path, against the previous week.
 *
 * `pct` is omitted when the previous week was zero or absent: "up 100%" from nothing is not
 * a percentage, and printing one invents a baseline. `isNew` carries that case instead.
 */
export function deltasByPath(
  current: readonly GroupNode[],
  previous: readonly GroupNode[],
): Map<string, Delta> {
  const before = new Map<string, number>();
  const collect = (nodes: readonly GroupNode[]) => {
    for (const n of nodes) {
      before.set(n.path, n.totals.totalTokens);
      collect(n.children);
    }
  };
  collect(previous);

  const out = new Map<string, Delta>();
  const walk = (nodes: readonly GroupNode[]) => {
    for (const n of nodes) {
      const was = before.get(n.path);
      const now = n.totals.totalTokens;
      if (was === undefined || was === 0) {
        out.set(n.path, { absolute: now, isNew: was === undefined });
      } else {
        out.set(n.path, {
          absolute: now - was,
          pct: ((now - was) / was) * 100,
          isNew: false,
        });
      }
      walk(n.children);
    }
  };
  walk(current);
  return out;
}

/** Signed, human-readable delta. Undefined when there is nothing meaningful to compare. */
export function formatDelta(delta: Delta | undefined): string | undefined {
  if (!delta) return undefined;
  if (delta.isNew) return 'new';
  if (delta.pct === undefined) return undefined;
  const digits = Math.abs(delta.pct) >= 10 ? 0 : 1;
  return `${delta.pct > 0 ? '+' : ''}${delta.pct.toFixed(digits)}%`;
}

/**
 * Everything the derivation panel states, computed FROM the response rather than written as
 * static copy. That is what makes it audit evidence rather than reassurance.
 */
export type Derivation = {
  /**
   * "2026-W33" for a week; "2026-08-13" / "2026-08" for a day / month, with a
   * " (America/Chicago)"-style suffix when cut at non-UTC midnights. Feeds titles
   * and stamps as-is; exportFilename sanitizes the suffix's space, parens and '/'.
   */
  period: string;
  periodKind: 'week' | 'day' | 'month';
  /** The zone raw-backed periods were cut in; 'UTC' for weeks and tz-less requests. */
  periodTz: string;
  grain: string;
  grouping: string;
  /** Server-side filter, from the request. */
  filter: string;
  /**
   * Client-side narrowing applied to the rows before grouping.
   *
   * Carried here, not just rendered in the panel, because the EXPORT inherits it: a file
   * containing one person's rows must not describe itself as containing everyone's.
   */
  narrowed: string;
  grainRows: number;
  displayedGroups: number;
  summed: string[];
  notSummed: string[];
  retention: string;
  sentinel: string;
  /** Repos that have reported at some point but contributed nothing to this period. */
  silentThisWeek: string[];
  unattributedRows: number;
  /** Cost provenance: what every dollar figure shown actually is. */
  costBasis: string;
  /** Savings provenance: what the savings column and tile actually are. */
  savingsBasis: string;
};

export function describeDerivation(args: {
  period:
    | { kind: 'week'; isoYear: number; isoWeek: number }
    | { kind: 'day'; day: string; retentionWeeks: number; tz?: string }
    | { kind: 'month'; month: string; retentionWeeks: number; tz?: string };
  rows: readonly WeeklyRow[];
  /**
   * The week's rows BEFORE any view narrowing.
   *
   * Separate from `rows` because "which repos went silent" is a question about the WEEK, not
   * about the current view. Comparing narrowed rows against the global repo list accused
   * repos of silence the moment an analyst clicked a model or a person -- on the one panel
   * whose whole job is not to mislead. Defaults to `rows` so an unnarrowed caller is
   * unaffected.
   */
  allRows?: readonly WeeklyRow[];
  groups: readonly GroupNode[];
  dims: readonly Dimension[];
  filter?: { field: 'author' | 'trigger'; login: string };
  focus?: readonly { dim: Dimension; value: string }[];
  knownRepos: readonly { org: string; repo: string }[];
}): Derivation {
  const { period, rows, groups, dims, filter, focus, knownRepos } = args;
  const present = new Set(
    (args.allRows ?? rows).map(r => `${r.org}/${r.repo}`),
  );
  const countNodes = (nodes: readonly GroupNode[]): number =>
    nodes.reduce((n, g) => n + 1 + countNodes(g.children), 0);

  return {
    // The zone travels with the period name: an exported "2026-08-13" cut at
    // Chicago midnights is a different set of rows than the UTC one.
    period:
      period.kind === 'week'
        ? `${period.isoYear}-W${String(period.isoWeek).padStart(2, '0')}`
        : `${period.kind === 'day' ? period.day : period.month}${
            period.tz && period.tz !== 'UTC' ? ` (${period.tz})` : ''
          }`,
    periodKind: period.kind,
    periodTz: period.kind === 'week' ? 'UTC' : period.tz ?? 'UTC',
    grain:
      period.kind === 'week'
        ? 'one row per (org, repo, model, PR author, run trigger, PR) per UTC ISO ' +
          'week, aggregated at read time from the raw per-run rows'
        : `one row per (org, repo, model, PR author, run trigger, PR) for one ` +
          `calendar ${period.kind} in ${period.tz ?? 'UTC'}, aggregated at read ` +
          `time from the raw per-run rows`,
    grouping: dims.map(d => DIMENSION_LABELS[d]).join(' then '),
    filter: filter
      ? `${DIMENSION_LABELS[filter.field]} = ${filter.login}`
      : 'none (every actor included)',
    narrowed: focus?.length
      ? focus.map(f => `${DIMENSION_LABELS[f.dim]} = ${f.value}`).join('; ')
      : 'nothing',
    grainRows: rows.length,
    displayedGroups: countNodes(groups),
    summed: [
      'input, output, cache-write and cache-read tokens',
      'messages',
      'run counts, but only within a single model',
    ],
    notSummed: [
      'run counts across models: one run reports once per model, so adding them counts it twice',
    ],
    retention:
      period.kind === 'week'
        ? 'every view reads the raw per-run rows, which are pruned after 12 weeks; weekly rollups are kept indefinitely as the archival record, and only rolled-up weeks are ever pruned'
        : `${period.kind} views read the raw per-run rows, which are pruned after ${period.retentionWeeks} weeks -- older ${period.kind}s exist only as weekly rollups` +
          (period.kind === 'month'
            ? '; a month at the retention edge may be PARTIAL (its early days pruned)'
            : ''),
    sentinel: `${UNKNOWN_ACTOR} means the run had no PR author or no resolvable trigger: a manual dispatch, or a bot acting outside a PR`,
    silentThisWeek: knownRepos
      .map(r => `${r.org}/${r.repo}`)
      .filter(k => !present.has(k))
      .sort(),
    unattributedRows: rows.filter(
      r => r.author === UNKNOWN_ACTOR || r.trigger === UNKNOWN_ACTOR,
    ).length,
    costBasis:
      "the runs' own reported metering (total_cost_usd), counted once per " +
      'distinct run; runs from emitters predating the cost field carry none ' +
      '(coverage is shown wherever it is partial). No price table exists in ' +
      'this tool.',
    savingsBasis:
      "the runs' own reported savings from intervention-attributable (counted) " +
      'methods only, one figure per savings method per run, counted once per ' +
      'distinct run and consolidated across methods where one figure is shown. ' +
      'Ambient pricing effects (prompt-cache) are excluded from every group ' +
      'figure except the prompt-cache row itself under a savings-method ' +
      'grouping — that vendor discount already lives in the cost column. ' +
      'Only runs that report a ' +
      'counted method carry any; coverage is shown wherever it is partial. ' +
      'Under a savings-method grouping, token, message and cost figures are ' +
      'withheld: a run can report several methods, so those groups overlap on ' +
      'runs and per-run figures cannot be attributed to one method without ' +
      'double-counting.',
  };
}

/**
 * RFC 4180 field: quoted whenever it could otherwise change the shape of the
 * row, and neutralized against spreadsheet formula injection (OWASP): a
 * leading `=` `+` `@` tab or CR — or a `-` that does not begin a plain
 * negative number — makes Excel/Sheets evaluate the cell, and the export
 * carries producer-controlled identifiers (org, repo, author, trigger, and
 * the savings method, whose charset admits a leading `@`/`-`). The leading
 * apostrophe is the standard escape: spreadsheets hide it and treat the cell
 * as text; a text reader sees an explicit escape. Genuine negative numbers
 * (the delta column exports "-92.0") stay numeric — the negative-number
 * carve-out is safe because every operator a `-`-led formula needs (`+ * (`)
 * is outside the identifier charset, so `-2` is a number and `-anything-else`
 * is neutralized.
 */
export function csvField(value: string | number | undefined): string {
  if (value === undefined) return '';
  const s = String(value);
  const neutralized =
    /^[=+@\t\r]/.test(s) || (/^-/.test(s) && !/^-\d+(\.\d+)?$/.test(s))
      ? `'${s}`
      : s;
  return /[",\r\n]/.test(neutralized)
    ? `"${neutralized.replace(/"/g, '""')}"`
    : neutralized;
}

/**
 * Provenance header carried by every export.
 *
 * Comment lines, so a spreadsheet import can skip them, while a file found on disk months
 * later still says which week it covers, what was filtered out, and which column must not
 * be summed.
 */
function stamp(lines: readonly string[]): string {
  return lines.map(l => `# ${l}`).join('\r\n');
}

export type ExportContext = {
  generatedAt: Date;
  derivation: Derivation;
  /** Active display filter, stamped so a pruned export cannot pass as complete. */
  rowFilter?: string;
};

/** CSV of exactly what is on screen, at the current grouping. */
export function toCsvShownView(
  groups: readonly GroupNode[],
  dims: readonly Dimension[],
  ctx: ExportContext,
  deltas?: Map<string, Delta>,
): string {
  const header = [
    ...dims.map(d => DIMENSION_LABELS[d]),
    'level',
    'total tokens',
    'input',
    'output',
    'cache write',
    'cache read',
    'messages',
    'reported cost usd',
    'cost coverage',
    'reported savings usd',
    'savings coverage',
    'runs (exact only)',
    'runs withheld because',
    'findings',
    'findings fixed',
    'fix rate %',
    'findings declined',
    'review quality withheld because',
    'change vs previous period %',
    'grain rows',
  ];

  const out: string[] = [];
  const walk = (nodes: readonly GroupNode[], trail: readonly string[]) => {
    for (const n of nodes) {
      const path = [...trail, n.value];
      const d = deltas?.get(n.path);
      out.push(
        [
          ...dims.map((_, i) => path[i] ?? ''),
          DIMENSION_LABELS[n.dim],
          // Withheld nodes export BLANK token/message figures, mirroring the
          // on-screen em dashes: a savings-method group's totals are
          // structurally zeroed (methods overlap on runs), and "0 total
          // tokens" in a file is a confidently wrong number, not a blank.
          ...(n.tokensWithheldBy
            ? ['', '', '', '', '', '']
            : [
                n.totals.totalTokens,
                n.totals.inputTokens,
                n.totals.outputTokens,
                n.totals.cacheWriteTokens,
                n.totals.cacheReadTokens,
                n.totals.msgs,
              ]),
          n.reportedCostUsd !== undefined ? n.reportedCostUsd.toFixed(4) : '',
          // Coverage travels even when the cost cell is blank: "0 of N runs"
          // is what distinguishes no-cost-data from model-withheld.
          n.costedRuns !== undefined &&
          n.totalRuns !== undefined &&
          n.costedRuns < n.totalRuns
            ? `${n.costedRuns} of ${n.totalRuns} runs`
            : '',
          n.savingsUsd !== undefined ? n.savingsUsd.toFixed(4) : '',
          n.savedRuns !== undefined &&
          n.totalRuns !== undefined &&
          n.savedRuns < n.totalRuns
            ? `${n.savedRuns} of ${n.totalRuns} runs`
            : '',
          n.runCount ?? '',
          n.runCountBlockedBy ? 'group spans more than one model' : '',
          // Same exact-or-blank contract as every other column: a withheld
          // quality cell exports blanks plus its reason, never zeros.
          n.review !== undefined ? n.review.findings : '',
          n.review !== undefined ? n.review.fixed : '',
          n.review?.fixRate !== undefined ? (n.review.fixRate * 100).toFixed(1) : '',
          n.review !== undefined ? n.review.declined : '',
          n.reviewWithheldBy ?? '',
          // Withheld nodes get no delta, mirroring the on-screen guard: the
          // baseline tree has no savings-method paths, so every method row
          // would export as a false 'new' forever.
          d === undefined || n.tokensWithheldBy
            ? ''
            : d.isNew
            ? 'new'
            : d.pct?.toFixed(1) ?? '',
          n.rowCount,
        ]
          .map(csvField)
          .join(','),
      );
      walk(n.children, path);
    }
  };
  walk(groups, []);

  return [
    stamp([
      'Claude usage - shown view',
      `generated ${ctx.generatedAt.toISOString()}`,
      `period ${ctx.derivation.period} (${ctx.derivation.periodKind})`,
      `grouping ${ctx.derivation.grouping}`,
      `server-side filter ${ctx.derivation.filter}`,
      `narrowed in view to ${ctx.derivation.narrowed}`,
      'runs and reported cost are blank where a group spans more than one model: a run reports once per model, so neither can be split there',
      `cost is ${ctx.derivation.costBasis}`,
      `savings are ${ctx.derivation.savingsBasis}`,
      ...(ctx.rowFilter
        ? [
            `row filter "${ctx.rowFilter}" -- non-matching rows are pruned for display; every exported figure is still that group's full total`,
          ]
        : []),
    ]),
    header.map(csvField).join(','),
    ...out,
  ].join('\r\n');
}

/** CSV of the un-collapsed grain, so a spreadsheet can re-derive any aggregate. */
const isoStampExact = (d: Derivation): boolean =>
  d.periodKind === 'week' || (d.periodKind === 'day' && d.periodTz === 'UTC');

export function toCsvGrain(rows: readonly WeeklyRow[], ctx: ExportContext): string {
  const header = [
    'iso year',
    'iso week',
    'org',
    'repo',
    'model',
    'pr author',
    'run trigger',
    'run type',
    'messages',
    'input tokens',
    'output tokens',
    'cache write tokens',
    'cache read tokens',
    'runs',
  ];
  return [
    stamp([
      'Claude usage - full grain',
      `generated ${ctx.generatedAt.toISOString()}`,
      `period ${ctx.derivation.period} (${ctx.derivation.periodKind})`,
      `server-side filter ${ctx.derivation.filter}`,
      `narrowed in view to ${ctx.derivation.narrowed}`,
      `grain: ${ctx.derivation.grain}`,
      'runs is exact at this grain; do not sum it across models',
      'no cost column at this grain: a run reports its cost once, with no per-model split at the source -- use the shown-view export or the panel for reported cost',
    ]),
    header.map(csvField).join(','),
    ...rows.map(r =>
      [
        // The ISO week cells appear only where they are guaranteed true: week
        // views (trivially) and UTC day views (a UTC day lies in one ISO week).
        // A month spans several weeks, and a ZONED day can straddle a UTC week
        // boundary -- both would stamp rows with a week a reader would trust.
        isoStampExact(ctx.derivation) ? r.isoYear : '',
        isoStampExact(ctx.derivation) ? r.isoWeek : '',
        r.org,
        r.repo,
        r.model,
        r.author,
        r.trigger,
        r.job ?? '',
        r.msgs,
        r.inputTokens,
        r.outputTokens,
        r.cacheWriteTokens,
        r.cacheReadTokens,
        r.runCount,
      ]
        .map(csvField)
        .join(','),
    ),
  ].join('\r\n');
}

/** Filename that sorts chronologically and says what it holds. */
export function exportFilename(
  kind: 'shown' | 'grain',
  derivation: Derivation,
): string {
  // Both scopes feed the name. A file holding one person's rows must not be called "all":
  // the filename is the first thing a reader sees months later, and it has to be true.
  const parts = [
    derivation.filter === 'none (every actor included)' ? '' : derivation.filter,
    derivation.narrowed === 'nothing' ? '' : derivation.narrowed,
  ]
    .filter(Boolean)
    .join('-');
  const scope = parts
    ? parts.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase()
    : 'all';
  // Same sanitation as scope: the period now carries a zone suffix like
  // " (America/Chicago)", and a raw "/" or space in a download name mangles
  // browser-dependently.
  const period = derivation.period
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `claude-usage-${period}-${kind}-${scope}.csv`;
}
